/**
 * The project switcher, and the create-project flow.
 *
 * Sits ABOVE the repo switcher, because a project contains its connections — the other order implies a
 * repo can span projects, which is exactly what the one-repo-per-project rule forbids.
 *
 * A platform admin sees projects they administer without being a member of. Those are marked rather
 * than silently mixed in: acting inside someone else's project should never look identical to owning
 * it.
 *
 * Styled with the same design tokens as the rest of the sidebar. A second visual language for one
 * control is how a UI starts looking assembled rather than designed.
 */
import { useState, useRef, useEffect, type JSX } from 'react'
import clsx from 'clsx'
import { RectangleStackIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline'
import { projectSlug, projectIdProblem } from '@stride/shared'
import { useProject } from '../../context/ProjectContext'
import { useSession } from '../../context/SessionContext'
import { apiPost, ApiError } from '../../lib/apiClient'

function CreateProjectForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }): JSX.Element {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const slug = projectSlug(name)
  // The SAME validator the server uses, so a preview cannot promise an id the server then rejects.
  const problem = name.trim().length === 0 ? null : projectIdProblem(slug)
  const canSubmit = !busy && name.trim().length > 0 && problem === null

  const submit = async (): Promise<void> => {
    setBusy(true); setError(null)
    try {
      await apiPost('/api/projects', { name: name.trim() })
      onCreated()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the project.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="p-2 border-t border-border" data-testid="create-project-form">
      <label className="block text-xs text-fg-subtle mb-1" htmlFor="new-project-name">
        New project name
      </label>
      <input
        id="new-project-name"
        className="w-full text-sm px-2 py-1 rounded bg-surface-1 border border-border text-fg"
        value={name}
        onChange={(e) => { setName(e.target.value) }}
        onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void submit() }}
        placeholder="Acme Migration"
        autoFocus
      />
      {/* Shown while typing because the id is PERMANENT — it partitions everything the project owns,
          so renaming later cannot change it. Better to see it before committing than to discover it. */}
      {slug.length > 0 && problem === null && (
        <p className="mt-1 text-xs text-fg-subtle">
          id <code className="text-fg-muted">{slug}</code> — permanent; renaming won’t change it.
        </p>
      )}
      {problem !== null && <p className="mt-1 text-xs text-warning">{problem}</p>}
      {error !== null && <p className="mt-1 text-xs text-danger" role="alert">{error}</p>}
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className="text-xs px-2 py-1 rounded bg-accent text-white disabled:opacity-50"
          disabled={!canSubmit}
          onClick={() => { void submit() }}
          data-testid="create-project-submit"
        >
          {busy ? 'Creating…' : 'Create'}
        </button>
        <button type="button" className="text-xs px-2 py-1 rounded text-fg-subtle" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

export function ProjectSwitcher({ collapsed }: { collapsed: boolean }): JSX.Element | null {
  const { projects, active, switchTo, refresh } = useProject()
  const { me } = useSession()
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  /**
   * Whether this caller may create a project at all.
   *
   * `can.administer` is the server's own answer — install-wide authority, not project admin. Re-deriving
   * the rule here would be one edit away from disagreeing with the route that enforces it.
   */
  const canCreate = me?.can.administer === true
  const containerRef = useRef<HTMLDivElement>(null)

  // Close on an outside click, matching the repo switcher's behaviour.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false); setCreating(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('mousedown', onDown) }
  }, [open])

  // Render nothing until the session resolves. A "no project" state here would flash on every load,
  // because /api/me arrives a moment after first paint.
  if (projects.length === 0 && active === null) return null

  const label = active?.name ?? 'Select a project'

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => { setOpen((o) => !o) }}
        aria-haspopup="menu"
        aria-expanded={open}
        className={clsx(
          'w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm',
          'text-fg-muted hover:bg-surface-2 hover:text-fg transition-colors',
          collapsed ? 'justify-center' : '',
        )}
        title={collapsed ? label : undefined}
        data-testid="project-switcher"
      >
        <RectangleStackIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {!collapsed && (
          <span className="truncate flex-1 text-left" data-testid="active-project-name">{label}</span>
        )}
        {!collapsed && <ChevronDownIcon className="h-4 w-4 shrink-0 text-fg-subtle" />}
      </button>

      {open && (
        <div
          role="menu"
          data-testid="project-switcher-menu"
          className="absolute z-20 mt-1 left-0 right-0 min-w-[14rem] bg-surface-2 border border-border rounded-md shadow-lg py-1"
        >
          <ul className="max-h-64 overflow-y-auto">
            {projects.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  role="menuitem"
                  className={clsx(
                    'w-full flex items-center gap-2 text-left px-3 py-1.5 text-sm hover:bg-surface-3',
                    p.id === active?.id ? 'text-accent' : 'text-fg',
                  )}
                  onClick={() => { setOpen(false); switchTo(p.id) }}
                >
                  <span className="truncate flex-1">{p.name}</span>
                  {p.via === 'platform-admin' && (
                    <span
                      className="text-[10px] uppercase tracking-wide text-warning shrink-0"
                      title="You see this as a platform admin, not as a member of it"
                    >
                      admin
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          {/*
            Creating a project is ADMINISTRATOR-only, so the control is hidden rather than shown and
            refused.

            It used to be offered to everyone, which was harmless while anyone could create one. Now the
            server answers 403, and an enabled button that always fails is the same defect as a permission
            nothing enforces — it tells the user something untrue about what they can do. The replacement
            line says who to ask, which is the actionable version.
          */}
          {!canCreate ? (
            <p
              className="px-3 py-2 text-[11px] leading-snug text-fg-subtle border-t border-border"
              data-testid="new-project-forbidden"
            >
              Only an administrator can create a project. Ask one to create it and invite you as its admin.
            </p>
          ) : creating
            ? (
              <CreateProjectForm
                onCreated={() => { setCreating(false); setOpen(false); refresh() }}
                onCancel={() => { setCreating(false) }}
              />
            )
            : (
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-accent border-t border-border hover:bg-surface-3"
                onClick={() => { setCreating(true) }}
                data-testid="new-project"
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                New project
              </button>
            )}
        </div>
      )}
    </div>
  )
}
